"use client";
import * as React from "react";
import { inputVariants } from "./variants";
import { VariantProps } from "class-variance-authority";
import { cn, debounce } from "@/lib/utils";
import { Spinner } from "./spinner";
import { useTextShortcuts } from "@/components/ui/use-text-shortcuts";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  start?: React.ReactNode;
  end?: React.ReactNode;
  label?: React.ReactNode;
  onChangeText?: (text: string) => void;
  focus?: boolean;
  value?: string;
  allowEditing?: boolean;
  outerClassName?: string;
  inputClassName?: string;
  onSave?: () => Promise<void>;
  hasError?: boolean;
  testId?: string;
  /**
   * Symbol shortcuts (`->` to `→`) and the `:` emoji menu while typing
   * (BRE-8381). Defaults on for text inputs; pass `false` where the text is a
   * value (a URL, key, slug, search query) rather than prose.
   */
  textShortcuts?: boolean;
  /** The brand gradient shows through the text while AI fills the field. */
  generating?: boolean;
}

// bg-clip-text shows the gradient only through transparent glyphs.
const GENERATING_TEXT_CLASS = "bg-gradient-brev bg-clip-text text-transparent";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      outerClassName,
      inputClassName,
      type,
      start,
      end,
      onChangeText,
      label,
      variant,
      fontSize,
      size,
      value,
      focus = false,
      allowEditing = true,
      onSave,
      hasError = false,
      testId = "input",
      textShortcuts,
      generating = false,
      onChange: callerOnChange,
      onKeyDown,
      onSelect,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [isSaving, setIsSaving] = React.useState(false);
    const onSaveRef = React.useRef(onSave);

    React.useEffect(() => {
      onSaveRef.current = onSave;
    }, [onSave]);

    const debouncedOnSave = React.useMemo(
      () =>
        debounce(async () => {
          if (value && value.trim() !== "") {
            setIsSaving(true);
            if (onSaveRef.current) {
              await onSaveRef.current!();
            }
            setIsSaving(false);
          }
        }, 500),
      [value]
    );

    const saveOnChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        onChangeText?.(newValue);
        if (newValue.trim() !== "") {
          debouncedOnSave();
        }
      },
      [debouncedOnSave, onChangeText]
    );

    const shortcuts = useTextShortcuts<HTMLInputElement>({
      enabled:
        (textShortcuts ?? (!type || type === "text")) && allowEditing && !props.disabled && !props.readOnly,
      // A caller's own onChange replaces the onChangeText/onSave path rather than adding to it.
      onChange: callerOnChange ?? saveOnChange,
      onKeyDown,
      onSelect,
      onBlur
    });

    return (
      <div className={cn("", outerClassName)} data-testid={`${testId}-wrapper`}>
        {label && (
          <label
            className={`flex flex-col pl-2 my-2 text-xs`}
            data-testid={`${testId}-label`}
          >
            {label}
          </label>
        )}
        <div
          className={cn(
            inputVariants({ variant, fontSize, size, hasError }),
            generating && GENERATING_TEXT_CLASS,
            className
          )}
          data-testid={`${testId}-container`}
        >
          {start && <div className="pl-3 m-auto">{start}</div>}
          <input
            tabIndex={0}
            autoFocus={focus}
            type={type}
            inputMode={type === "number" ? "numeric" : undefined}
            // Placeholder color lives here, not in inputVariants: those land on
            // the wrapper div, which has no ::placeholder.
            className={cn(
              "py-0 flex w-full outline-none bg-transparent file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed",
              variant === "inline" || variant === "underline" ? "px-0" : size === "xs" ? "px-1.5" : size ? "px-3" : "px-2",
              inputClassName,
              generating && GENERATING_TEXT_CLASS,
              hasError && "placeholder:text-destructive-text"
            )}
            ref={ref}
            value={value}
            readOnly={!allowEditing}
            aria-invalid={hasError || undefined}
            data-testid={testId}
            {...props}
            {...shortcuts.fieldProps}
          />
          {shortcuts.menu}
          {end}
          {isSaving && <Spinner size="sm" />}
        </div>
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
